import React from 'react';

import { twMerge } from 'tailwind-merge';

import dayjs from 'dayjs';
import isTomorrow from 'dayjs/plugin/isTomorrow';

import { TbArchiveOff, TbBellExclamation } from 'react-icons/tb';

import Table from 'components-new/table/table';
import Banner from 'components-new/banner/banner';
import Icon from 'components-new/icon/icon';
import Typography from 'components-new/typography/typography';
import Tooltip from 'components-new/tooltip/tooltip';

import type { OrganizationJob } from 'state/remote/types';

import { useOrganizationWeekPlanner } from 'state/context/organization/OrganizationWeekPlanner';

import getIconForJobType from 'pages/organization/OrganizationWeekPlannerPage/utils/get-icon-for-job-type';

import { createLocale } from 'locales';

import getJobHourlyRates from 'utils/get-job-hourly-rates';

import JobPopover from '../job-popover/job-popover';

import CancelBulkJobsDialog from './cancel-bulk-jobs-dialog';

dayjs.extend(isTomorrow);

const globalLocalize = createLocale('global');
const localize = createLocale('organizationWeekPlannerLocales');

const PlannerTable: React.FC = () => {
  const { tableParams } = useOrganizationWeekPlanner();

  const {
    currentPage,
    currentPageNumber,
    currentPageSize,
    onNumberOfItemsPerPageChange,
    totalPages,
    onPageChange,
    isLoading,
    onSelectedItemsChange,
    isCancelBulkJobsDialogOpen,
    setIsCancelBulkJobsDialogOpen,
    onCancelBulkJobsDialogAcceptClicked,
    displayCompensationOnBulkCancel,
  } = tableParams;

  return (
    <>
      <Table
        selectable={false}
        currentPage={currentPageNumber}
        totalPages={totalPages}
        pageSizeOptions={[1, 5, 10, 50]}
        currentPageSize={currentPageSize}
        fields={[
          {
            dataClassName: 'px-0',
            name: 'alert',
            render: (_, item) => {
              const job = item as unknown as OrganizationJob;

              const alertInterestJobNoLongerAssigned48Hrs =
                job.is_interest &&
                !job.assigned_to &&
                dayjs(job.date).isBetween(dayjs(), dayjs().add(48, 'hour')) && // between now and 48 hrs from now
                localize('alertInterestJobNoLongerAssigned48HrsMessage');

              const alertInterestShownWithoutAssignedFreelancer =
                job.is_interest_shown &&
                !job.assigned_to &&
                !alertInterestJobNoLongerAssigned48Hrs &&
                localize('alertInterestShownWithoutAssignedFreelancerMessage');

              const alertPersonalJobStartsTomorrowNotAccepted =
                job.is_personal &&
                !job.assigned_to &&
                dayjs(job.date).isTomorrow() &&
                localize('alertPersonalJobStartsTomorrowNotAcceptedMessage');

              if (
                alertInterestJobNoLongerAssigned48Hrs ||
                alertInterestShownWithoutAssignedFreelancer ||
                alertPersonalJobStartsTomorrowNotAccepted
              ) {
                return (
                  <div className="h-full flex items-center">
                    <Tooltip
                      tooltipTriggerProps={{
                        asChild: false,
                        className: 'cursor-default',
                      }}
                      text={
                        alertInterestJobNoLongerAssigned48Hrs ||
                        alertInterestShownWithoutAssignedFreelancer ||
                        alertPersonalJobStartsTomorrowNotAccepted ||
                        ''
                      }
                    >
                      <Icon
                        size={20}
                        icon={TbBellExclamation}
                        className={twMerge(
                          'ml-auto',
                          alertInterestShownWithoutAssignedFreelancer &&
                            'text-warning-400',
                          (alertPersonalJobStartsTomorrowNotAccepted ||
                            alertInterestJobNoLongerAssigned48Hrs) &&
                            'text-destructive-400',
                        )}
                      />
                    </Tooltip>
                  </div>
                );
              }
              return <></>;
            },
            title: '',
          },
          {
            dataClassName: 'min-w-[140px]',
            name: 'date',
            render: (value) => {
              return (
                <Typography variant="body-large" as="p">
                  {dayjs(value as string).format('DD MMMM YYYY')}
                </Typography>
              );
            },
            sortable: false,
            title: globalLocalize('date'),
          },
          {
            dataClassName: 'min-w-[100px]',
            name: 'workHours',
            render: (_, item) => {
              const job = item as unknown as OrganizationJob;

              return (
                <Typography variant="body-large" as="span">
                  {job.workHours}
                </Typography>
              );
            },
            sortable: false,
            title: globalLocalize('time'),
          },
          {
            dataClassName: 'table-cell items-[top]',
            name: 'status',
            render: (value) => {
              return (
                <Banner
                  title={
                    value === 'open'
                      ? globalLocalize('open')
                      : globalLocalize('assigned')
                  }
                  variant={value === 'open' ? 'destructive' : 'success'}
                  className="inline-flex mb-[6px]"
                />
              );
            },
            sortable: false,
            title: 'Status',
          },
          {
            name: 'location',
            render(_, item) {
              const job = item as unknown as OrganizationJob;
              return (
                <Typography
                  key={`${job.location.id} ${job.date} ${job.assigned_to}`}
                  as="p"
                  variant="body-large"
                  className="text-left py-1"
                >
                  {job.location.name}
                </Typography>
              );
            },
            sortable: false,
            title: globalLocalize('location'),
          },
          {
            name: 'assigned_to',
            render(_, item) {
              const job = item as unknown as OrganizationJob;
              return (
                <>
                  {item.assigned_to && (
                    <Typography
                      as="p"
                      variant="body-large"
                      className="text-left py-1"
                    >
                      {job.assigned_to?.name}
                    </Typography>
                  )}
                </>
              );
            },
            sortable: false,
            title: localize('freelancerName'),
          },
          {
            name: 'competence',
            render(_, item) {
              const job = item as unknown as OrganizationJob;
              return (
                <Typography
                  key={job.id}
                  as="p"
                  variant="body-large"
                  className="text-left py-1"
                >
                  {job.competence}
                </Typography>
              );
            },
            sortable: false,
            title: globalLocalize('competence'),
          },
          {
            dataClassName: 'min-w-[200px]',
            name: 'hourlyRate',
            render(_, item) {
              const job = item as unknown as OrganizationJob;
              const tarief = getJobHourlyRates(job);
              return (
                <Typography
                  key={job.id}
                  as="p"
                  variant="body-large"
                  className="text-left py-1"
                >
                  {tarief}
                </Typography>
              );
            },
            sortable: false,
            title: globalLocalize('hourlyRate'),
          },
          {
            dataClassName: '',
            name: 'type',
            render: (_, item) => {
              let jobIcon = getIconForJobType('urgent');
              let text: string = '';
              let tooltipContent: string = '';

              if (item.is_interest) {
                jobIcon = getIconForJobType('interest');
                text = globalLocalize('interest');
                tooltipContent = localize('thisIsIntrestAssignment');
              }

              if (item.is_urgent && !item.is_personal) {
                jobIcon = getIconForJobType('urgent');
                text = globalLocalize('urgent');
                tooltipContent = localize('thisIsUrgentAssignment');
              }

              if (item.is_personal) {
                jobIcon = getIconForJobType('personal');
                text = globalLocalize('personal');
                tooltipContent = localize('thisIsPersonalAssignment');
              }

              return (
                <div className="flex items-center gap-x-1">
                  <Tooltip
                    text={tooltipContent}
                    tooltipTriggerProps={{
                      asChild: false,
                      className: 'cursor-default',
                    }}
                  >
                    <Icon icon={jobIcon} size={18} color="#4B484D" />
                  </Tooltip>
                  <Typography
                    variant="body-large"
                    as="span"
                    className="text-gray600"
                  >
                    {text}
                  </Typography>
                </div>
              );
            },
            sortable: false,
            title: 'Job type',
          },
          {
            dataClassName: 'pr-6',
            headerClassName: 'w-12',
            name: 'more',
            render: (_, item) => {
              return <JobPopover job={item as unknown as OrganizationJob} />;
            },
            title: '',
          },
        ]}
        data={
          currentPage as unknown as Array<
            Record<string, string | number | undefined>
          >
        }
        hasNextPage={currentPageNumber < totalPages}
        hasPreviousPage={currentPageNumber > 1}
        onPageChange={onPageChange}
        onNumberOfItemsPerPageChange={onNumberOfItemsPerPageChange}
        isLoading={isLoading}
        onSelectedItemsChange={onSelectedItemsChange}
        emptyStateProps={{
          errorText: localize('noResultsTable'),
          icon: TbArchiveOff,
          title: localize('noResultsTableTitle'),
        }}
      />
      <CancelBulkJobsDialog
        isOpen={isCancelBulkJobsDialogOpen}
        onAcceptClicked={onCancelBulkJobsDialogAcceptClicked}
        onBackClicked={() => setIsCancelBulkJobsDialogOpen(false)}
        onCloseClicked={() => setIsCancelBulkJobsDialogOpen(false)}
        variant={displayCompensationOnBulkCancel ? 'compensate' : undefined}
      />
    </>
  );
};

export default PlannerTable;
